import { db, functions } from "@/main";
import { defineOwnRoleSettingsClientAccess } from "@/permissions/ClientPermissions";
import { LicenceInfoV3 } from "../models/LicenceInfoV3";
import { RoleSettings } from "../models/RoleSettings";
import { getClientRef, getClientsAdministrationRef } from "./ClientRepository";
import { RoleSettingsModule } from "@/store/modules/RoleSettingsModule";

function getRoleSettingsRef() {
  const clientsAdministrationRef = getClientsAdministrationRef();

  const documentRef =
    clientsAdministrationRef === "" ||
    defineOwnRoleSettingsClientAccess()
      ? getClientRef()
      : clientsAdministrationRef;

  return db
    .doc(documentRef)
    .collection("settings")
    .doc("roleSettings");
}

export interface SaveRoleSettingsResponse {
  wasSuccessful: boolean;
  licenceInfo?: LicenceInfoV3;
}

export async function saveRoleSettings(roleSettings: RoleSettings, updateLicences: boolean) {
  const saveRoleSettings = functions.httpsCallable("saveRoleSettings");

  return (
    await saveRoleSettings({
      roleSettings: JSON.stringify(roleSettings),
      updateLicences: updateLicences,
    })
  ).data as SaveRoleSettingsResponse;
}

export function observeRoleSettings(
  onNewRoleSettings: (roleSettings: RoleSettings) => void
) {
  return getRoleSettingsRef().onSnapshot((snapshot) => {
    if (snapshot.exists) {
      const roleSettings = snapshot.data() as RoleSettings;
      onNewRoleSettings(roleSettings);
    }
  });
}

/**
 * Populates {@link RoleSettingsModule} once, if the live observer has not already done so.
 *
 * Needed by anything that must **deny** on a role-resolved capability before the app has finished
 * starting up. `RoleSettingsModule` begins on defaults that grant nothing, and the observer that
 * replaces them is wired up in `Scaffold` — which is the route component for `/main`, so it cannot
 * mount until `router.beforeEach` has resolved. A guard that waited for the observer would therefore
 * deadlock on a hard page load; a guard that did not wait would deny a user whose capabilities had
 * simply not arrived yet. A one-shot read is the only option that does neither.
 *
 * Cheap: the module keeps the result, so this reads Firestore at most once per session, and the
 * observer takes over afterwards.
 *
 * A missing document or a failed read leaves the defaults in place, so callers stay fail-closed.
 */
export async function ensureRoleSettingsLoaded(): Promise<void> {
  if (RoleSettingsModule.hasLoaded) {
    return;
  }

  try {
    const snapshot = await getRoleSettingsRef().get();

    if (snapshot.exists) {
      RoleSettingsModule.setRoleSettings(snapshot.data() as RoleSettings);
    }
  } catch {
    // Leave the defaults; the caller decides what an unknown capability means.
  }
}

